import React, { Children } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
export function PreOrderConfirmation() {
  const {
    user,
    loading
  } = useAuth();
  const {
    items
  } = useCart();
  // Guard: if someone deep-links here without a session, send them to shop.
  if (!loading && !user) {
    return <Navigate to="/shop" replace data-id="element-1806" />;
  }
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1807">
      {/* HERO */}
      <section className="bg-bodybg pt-40 pb-16 px-6 border-b border-darkgray" data-id="element-1808">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1809">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1810">
            <motion.div variants={fadeUp} className="w-16 h-16 rounded-full bg-tealslate/15 mx-auto mb-8 flex items-center justify-center" data-id="element-1811">
              <CheckCircle2 className="w-8 h-8 text-tealslate" strokeWidth={2} data-id="element-1812" />
            </motion.div>
            <motion.p variants={fadeUp} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1813">
              Pre-Order Received
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1814">
              Thank you for your pre-order.
            </motion.h1>
            <motion.div variants={fadeUp} className="w-16 h-0.5 bg-tealslate mx-auto mb-6" data-id="element-1815" />
            <motion.p variants={fadeUp} className="text-base text-[#D8D8D0] max-w-xl mx-auto leading-relaxed" data-id="element-1816">
              We'll notify you by email when it's time to check out and complete
              your order. No payment has been taken.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="bg-[#F5F5F0] py-16 md:py-20 px-6" data-id="element-1817">
        <div className="max-w-2xl mx-auto" data-id="element-1818">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="bg-white border-l-4 border-tealslate p-8 mb-8" data-id="element-1819">
            <h2 className="font-serif text-2xl text-[#1F1F1F] mb-4" data-id="element-1820">
              What happens next?
            </h2>
            <ul className="space-y-3 text-sm text-[#3B3B3B] leading-relaxed" data-id="element-1821">
              <li className="flex gap-3" data-id="element-1822">
                <span className="text-tealslate font-bold shrink-0" data-id="element-1823">1.</span>
                <span data-id="element-1824">
                  A confirmation email is on its way to{' '}
                  <span className="font-bold text-[#1F1F1F]" data-id="element-1825">
                    {user?.email}
                  </span>
                  .
                </span>
              </li>
              <li className="flex gap-3" data-id="element-1826">
                <span className="text-tealslate font-bold shrink-0" data-id="element-1827">2.</span>
                <span data-id="element-1828">
                  Your reserved items stay saved in your cart — no need to
                  re-add them later.
                </span>
              </li>
              <li className="flex gap-3" data-id="element-1829">
                <span className="text-tealslate font-bold shrink-0" data-id="element-1830">3.</span>
                <span data-id="element-1831">
                  We'll email you as soon as inventory is ready so you can
                  complete your order.
                </span>
              </li>
            </ul>
          </motion.div>

          {items.length > 0 && <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="bg-charcoal text-white p-8 border-l-4 border-tealslate mb-10" data-id="element-1832">
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-5" data-id="element-1833">
                Your Reserved Items
              </p>
              <ul className="space-y-3" data-id="element-1834">
                {items.map(item => <li key={item.productId} className="flex justify-between items-start gap-4 text-sm" data-id="element-1835">
                    <div data-id="element-1836">
                      <p className="font-serif text-base text-white" data-id="element-1837">
                        {item.name}
                      </p>
                      <p className="text-xs text-[#9CA3AF] mt-1" data-id="element-1838">
                        Qty {item.quantity}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </motion.div>}

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="flex flex-col sm:flex-row gap-4" data-id="element-1839">
            <Link to="/shop" className="flex-1 px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-3" data-id="element-1840">
              <ShoppingBag className="w-4 h-4" data-id="element-1841" />
              Back to Shop
            </Link>
            <Link to="/" className="flex-1 px-8 py-4 border border-[#1F1F1F] text-[#1F1F1F] text-sm font-bold uppercase tracking-wider hover:bg-[#1F1F1F] hover:text-white transition-colors inline-flex items-center justify-center gap-3" data-id="element-1842">
              Return Home <ArrowRight className="w-4 h-4" data-id="element-1843" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}